import {
  GridColumnResizeEvent,
  GridDetailRowProps,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid';
import { useCallback, useEffect, useState } from 'react';
import {
  useMultiPeriodGLSummaryColumns,
  useMultiPeriodGLSummarySubColumns,
} from 'src/assets/static/action_required_invoices/MultiPeriodGLSummaryColumns';
import GridWrapper from 'src/components/GridWrapper';
import { IGlSummary, IGlSummaryGropued } from 'src/services/useActionRequiredInvoicesApi';
import { fToMMDDYY } from 'src/utils/formatTime';

interface MultiPeriodGLSummaryProps {
  glSummaries: IGlSummary[];
}

const MultiPeriodGLSummary = ({ glSummaries }: MultiPeriodGLSummaryProps) => {
  const [data, setData] = useState(glSummaries);
  const multiPeriodGLSummaryColumns = useMultiPeriodGLSummaryColumns();
  const multiPeriodGLSummarySubColumns = useMultiPeriodGLSummarySubColumns();
  const [subordinateColumn, setSubordinateColumn] = useState(multiPeriodGLSummarySubColumns);

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props.dataItem.items}
        columns={subordinateColumn}
        DataItemKey="field"
        className="invisible-grid-column-header hide-scroll-bar"
      />
    ),

    [subordinateColumn]
  );

  function calculateSum(summary: IGlSummary, fieldName: string) {
    if (!summary.items || summary.items?.length === 0) {
      return summary[fieldName];
    }

    return summary.items.reduce((total, item: IGlSummaryGropued) => {
      if (item[fieldName] !== undefined) {
        return total + item[fieldName];
      }
      return total;
    }, 0);
  }

  useEffect(() => {
    let groupedByGLCd: { [key: string]: IGlSummary } = {};

    glSummaries.forEach((summary) => {
      const { glCd, rowId, id, budgetPeriodStartDate, budgetPeriodEndDate, glRemainingBudget } =
        summary;
      if (glCd !== undefined) {
        if (!groupedByGLCd[glCd]) {
          groupedByGLCd[glCd] = {
            glCd: glCd,
            description: summary.description,
            items: [],
            rowId: rowId,
            id: id,
            accountingPeriod: '',
            budgetPeriodEndDate: '',
            budgetPeriodStartDate: '',
            invoiceType: 'Multiple',
          };
        }
        groupedByGLCd[glCd].items?.push({
          ...summary,
          accountingPeriod: `${fToMMDDYY(budgetPeriodStartDate)}-${fToMMDDYY(budgetPeriodEndDate)}`,
          glRemainingBudget: glRemainingBudget || 0,
        });
      }
    });

    let processedGroupedGlSummaries: IGlSummary[] = Object.values(groupedByGLCd);

    let glSummariesWithGrandTotals = processedGroupedGlSummaries.map((summary) => {
      let poGlTotalSum = calculateSum(summary, 'poGlTotal');
      let otherDocGlTotalSum = calculateSum(summary, 'otherDocGlTotal');
      let currentDocGlTotalSum = calculateSum(summary, 'currentDocGlTotal');
      return {
        ...summary,
        poGlGrandTotal: poGlTotalSum,
        otherDocGlGrandTotal: otherDocGlTotalSum,
        currentDocGlGrandTotal: currentDocGlTotalSum,
      };
    });
    setData(glSummariesWithGrandTotals);
  }, [glSummaries]);

  const handleItemChange = useCallback((e: GridItemChangeEvent) => {
    const { rowId } = e.dataItem;
    const { field = '', value } = e;
    setData((prev) =>
      [...prev].map((item) => ({
        ...item,
        ...(rowId === item.rowId && {
          [field]: value,
        }),
      }))
    );
  }, []);

  const cellClickFns = useCallback(
    () => ({
      setData,
      data,
    }),
    [data]
  );

  const onColumnResize = useCallback((event: GridColumnResizeEvent) => {
    const { columns, index, end } = event;
    if (end) {
      if (index === 0) return;
      else {
        const width = Number(columns[index].width);
        setSubordinateColumn((prev) => {
          const cols = [...prev];
          cols[index - 1].width = width;
          return cols;
        });
      }
    }
  }, []);

  return (
    <GridWrapper
      columns={multiPeriodGLSummaryColumns}
      dataSource={data}
      DataItemKey="field"
      onItemChange={handleItemChange}
      detail={DetailComponent}
      cellClick={cellClickFns}
      resizable
      total={0}
      isCustomExpandableRow
      className="max-h-[60vh] auto-scrollbar"
      onColumnResize={onColumnResize}
    />
  );
};

export default MultiPeriodGLSummary;
